import type { ApiError, ErrorCode, ErrorResponse, OidcLinkRequired } from './schema';

/** Svi kodovi grešaka iz ugovora (redoslijed kao u `api/openapi.yaml`). */
export const ERROR_CODES = [
  'validation_failed',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'measurement_locked',
  'feature_disabled',
  'email_not_verified',
  'invalid_token',
  'internal',
  'sole_admin',
  'link_required',
  'no_account',
  'oidc_invalid',
  'oidc_email_unverified',
  'identity_mismatch',
  'provider_disabled',
] as const satisfies readonly ErrorCode[];

/** Kodovi grešaka za koje klijent nema smisla da ponavlja zahtjev. */
const NETWORK_CODE = 'network_error';

/**
 * Greška API poziva. `code` je prvi kod iz omotača i koristi se za i18n (`errors:<code>`).
 * Za mrežne greške (nema odgovora) `status` je 0, a `code` je `internal` uz `network = true`.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly errors: ApiError[];
  readonly code: ErrorCode;
  readonly requestId: string | undefined;
  readonly network: boolean;
  /** Sirovo tijelo odgovora (npr. `link` uz `409 link_required`). */
  readonly body: unknown;

  constructor(
    status: number,
    errors: ApiError[],
    opts: { requestId?: string; network?: boolean; cause?: unknown; body?: unknown } = {},
  ) {
    const first = errors[0];
    super(first ? `${first.code}: ${first.message}` : `HTTP ${status}`, { cause: opts.cause });
    this.name = 'ApiClientError';
    this.status = status;
    this.errors = errors;
    this.code = first?.code ?? 'internal';
    this.requestId = opts.requestId ?? first?.request_id;
    this.network = opts.network ?? false;
    this.body = opts.body;
  }

  /** Podaci za povezivanje naloga iz `409 link_required` (`POST /auth/oidc/{provider}`). */
  get linkRequired(): OidcLinkRequired | undefined {
    if (this.code !== 'link_required') return undefined;
    const link = (this.body as { link?: unknown } | undefined)?.link;
    return typeof link === 'object' &&
      link !== null &&
      typeof (link as { ticket?: unknown }).ticket === 'string'
      ? (link as OidcLinkRequired)
      : undefined;
  }

  /** Da li bilo koja greška u omotaču ima zadati kod. */
  has(code: ErrorCode): boolean {
    return this.errors.some((e) => e.code === code);
  }

  /** Mapa `polje -> kod` za prikaz grešaka uz polja forme. */
  fieldErrors(): Record<string, ErrorCode> {
    const out: Record<string, ErrorCode> = {};
    for (const e of this.errors) {
      if (e.field && !(e.field in out)) out[e.field] = e.code;
    }
    return out;
  }

  /** i18n ključ za prvu grešku (namespace `errors`). */
  get i18nKey(): string {
    return this.network ? `errors:${NETWORK_CODE}` : `errors:${this.code}`;
  }

  static network(cause: unknown, requestId?: string): ApiClientError {
    return new ApiClientError(0, [{ code: 'internal', message: 'network error' }], {
      network: true,
      cause,
      ...(requestId ? { requestId } : {}),
    });
  }

  /**
   * Da li korisniku prikazati šifru greške (request ID): mreža, 5xx i neočekivane greške; ne za greške
   * validacije forme, istekle sesije i rate limit (korisnik ih sam rješava).
   */
  get showsRequestId(): boolean {
    if (!this.requestId) return false;
    if (this.network || this.status >= 500) return true;
    return ![
      'validation_failed',
      'unauthorized',
      'rate_limited',
      'invalid_token',
      'email_not_verified',
    ].includes(this.code);
  }

  /** Da li grešku treba poslati u Sentry: samo mreža i 5xx (ne 4xx validacija/dozvole). */
  get isReportable(): boolean {
    return this.network || this.status >= 500;
  }

  static fromBody(status: number, body: unknown, requestId?: string): ApiClientError {
    if (isErrorResponse(body)) {
      return new ApiClientError(status, body.errors, { ...(requestId ? { requestId } : {}), body });
    }
    return new ApiClientError(
      status,
      [{ code: statusToCode(status), message: `HTTP ${status}` }],
      requestId ? { requestId } : {},
    );
  }
}

export function isErrorResponse(body: unknown): body is ErrorResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    Array.isArray((body as { errors?: unknown }).errors) &&
    (body as { errors: unknown[] }).errors.every(
      (e) =>
        typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string',
    )
  );
}

export function isApiClientError(err: unknown, code?: ErrorCode): err is ApiClientError {
  return err instanceof ApiClientError && (code === undefined || err.has(code));
}

export function statusToCode(status: number): ErrorCode {
  switch (status) {
    case 400:
    case 422:
      return 'validation_failed';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 429:
      return 'rate_limited';
    default:
      return 'internal';
  }
}
